import { Row, Col, Container } from "../common/components/Grid";
import { getJobImageData, removeImage, useJobImages } from "../reducers/jobImageSlice";
import useNetworkEffect from "../common/hooks/networkEffectHook";
import { useState } from "react";
import useNetwork from "../common/hooks/networkHook";

import { useTranslation } from "react-i18next";
import { safeHtml } from "../common/utils/safeHtml";
import useClientConfig from "../common/hooks/clientConfigHook";
import { DefaultButton } from "../common/components/DefaultButton";

import PropTypes from 'prop-types';
import TextInput from "../common/components/TextInput";
import ImageInput from "../common/components/ImageInput";
import useCredentials from "../common/hooks/credentialsHook";
import Icon from "../common/components/Icon";
import { Modal } from "../common/utils/dialogUtlities";

/**
 * Display job images and allow job image upload / capture
 * @component
 */
const JobImages = ({ jobid, disabled }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const images = useJobImages(jobid);
    const [fullData, setFullData] = useState();
    const [thumbnailData, setThumbnailData] = useState({});
    const [selectedImage, setSelectedImage] = useState(null);
    const [newCaption, setNewCaption] = useState();
    const [newImage, setNewImage] = useState();
    const selectedIdx = selectedImage ? images.findIndex(img => img.id === selectedImage.id) : -1;
    const canDelete = !disabled && credentials.access.hasFull('administrator');

    useNetworkEffect(network => {
        images.filter(img => !thumbnailData[img.id]).forEach(async img => {
            let data = await getJobImageData(network, img.jobid, img.filename, 300);
            setThumbnailData(thumbnailData => ({ ...thumbnailData, [img.id]: data }));
        });
    }, [images]);

    const selectImage = async image => {
        setSelectedImage(image);
        setFullData(null);
        setFullData(await getJobImageData(network, image.jobid, image.filename));
    }

    const doRemove = image => {
        removeImage(network, image);
        if (selectedIdx > 0) {
            selectImage(images[selectedIdx - 1]);
        } else if (selectedIdx < images.length - 1) {
            selectImage(images[selectedIdx + 1]);
        } else {
            selectImage();
        }
    }

    const addImage = (caption, image) => {
        const formData = new FormData();
        formData.append('file', network.dataURIToBlob(image));
        if (caption) {
            formData.append('caption', caption);
        }
        network.postData('/api/v1/jobimages/' + encodeURIComponent(jobid), formData);
        setNewCaption();
        setNewImage();
    }

    return <>
    { selectedImage ? <Modal show={true} size="lg">
        <Modal.Header>
            {t('image_one')}
        </Modal.Header>
        <Modal.Body style={{textAlign: 'center'}}>
            <Row>
                <Col xs="auto" style={{ marginTop: 'auto', marginBottom: 'auto', cursor: selectedIdx > 0 ? 'pointer' : null }} onClick={() => selectedIdx > 0 && selectImage(images[selectedIdx - 1])}>
                    {selectedIdx > 0 ? <Icon intent="left"></Icon> : safeHtml('&nbsp;')}
                </Col>
                <Col>
                    <Row>
                        <Col>
                            <img src={fullData ?? thumbnailData[selectedImage.id]} alt={selectedImage.caption} style={{maxWidth: '100%', maxHeight: 'calc(100vh - 300px)'}}></img>
                        </Col>
                    </Row>
                    { clientConfig.disableImageCaption ? null : <Row>
                        <Col>
                            {selectedImage.caption}
                        </Col>
                    </Row> }
                </Col>
                <Col xs="auto" style={{ marginTop: 'auto', marginBottom: 'auto', cursor: selectedIdx < images.length - 1 ? 'pointer' : null }} onClick={() => selectedIdx < images.length - 1 && selectImage(images[selectedIdx + 1])}>
                    {selectedIdx < images.length - 1 ? <Icon intent="right"></Icon> : safeHtml('&nbsp;')}
                </Col>
            </Row>
        </Modal.Body>
        <Modal.Footer>
            <Container className='w-100'>
                <Row>
                    <Col style={{ textAlign: 'left'}}>
                        {canDelete ? <DefaultButton kind="delete" onClick={() => doRemove(selectedImage)}></DefaultButton> : null}
                    </Col>
                    <Col style={{textAlign: 'end'}}>
                        <DefaultButton kind="close" onClick={() => { setFullData(null); setSelectedImage(null) }}></DefaultButton>
                    </Col>
                </Row>
            </Container>
        </Modal.Footer>
    </Modal> : null }
    <Row>
        <Col>
            <div style={{ display: "inline-flex", flexDirection: "row", flexWrap: "wrap"}}>
                {images?.length > 0 ? images.map(image => { 
                    return <div key={jobid.replace('-','') + '_' + image.id} className="m-2" style={{ maxWidth: '200px'}}>
                        <Row>
                            <Col>
                                {thumbnailData[image.id] ? <img src={thumbnailData[image.id]} alt={image.caption} onClick={() => selectImage(image)} style={{objectFit: 'contain', width: '200px', height: '200px', cursor: 'pointer'}}></img> : t('loading')}
                            </Col>
                        </Row>
                        { clientConfig.disableImageCaption ? null : <Row>
                            <Col>
                                {image.caption}
                            </Col>
                        </Row> }
                    </div>
                }) : <div>{t('noImages')}</div>}
                {disabled ? null : <div className="m-2" style={{maxWidth: '200px'}}>
                    <Row>
                        <Col>
                            <div style={{ width: '200px', height: '200px' }}>
                                <ImageInput value={newImage} height="200px" hideLabel onChange={setNewImage}></ImageInput>
                            </div>
                        </Col>
                    </Row>
                    <Row className="mt-3">
                        <Col>
                            {clientConfig.disableImageCaption ? null : <TextInput value={newCaption} placeholder={t('caption')} hideLabel onChange={setNewCaption} maxLength={256}></TextInput>}
                        </Col>
                        <Col xs="auto">
                            <DefaultButton kind="add" disabled={!newImage} onClick={() => addImage(newCaption, newImage)}></DefaultButton>
                        </Col>
                    </Row>
                </div> }
            </div>
        </Col>
    </Row>
    </>
};

JobImages.propTypes = {
    /**
     * The job ID whose images should be displayed
     */
    jobid: PropTypes.string
};

export default JobImages;